import type { OrgModelConfigFilter } from "../OrgLlmProviderRecord";
import type { OrganizationTier } from "../OrgRecord";

/**
 * Query key factory for org queries.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const orgQueryKeys = {
  all: () => ["orgs"] as const,

  /** Prefix for every paginated org listing. */
  lists: () => [...orgQueryKeys.all(), "list"] as const,
  list: (
    params: { search?: string; tier?: OrganizationTier | "all" },
    pageSize: number,
  ) =>
    [
      ...orgQueryKeys.lists(),
      params.search ?? "",
      params.tier ?? "all",
      pageSize,
    ] as const,

  /** Prefix for every org search. */
  searches: () => [...orgQueryKeys.all(), "search"] as const,
  search: (term: string) => [...orgQueryKeys.searches(), term] as const,

  /** The org record, and the prefix for everything else about that org. */
  org: (orgId: string | undefined) =>
    orgId === undefined
      ? ([...orgQueryKeys.all(), "org"] as const)
      : ([...orgQueryKeys.all(), "org", orgId] as const),

  /** Every member of the org, with their roles. */
  members: (orgId: string | undefined) =>
    [...orgQueryKeys.org(orgId), "members"] as const,

  /** The org's chat model list and its own connections. */
  llmProvider: (orgId: string) =>
    [...orgQueryKeys.org(orgId), "llmProvider"] as const,

  /** The Roboto-managed models and connection defaults the org's model settings pre-fill from. */
  llmProviderTemplates: (orgId: string) =>
    [...orgQueryKeys.org(orgId), "llmProviderTemplates"] as const,

  /** The deployment's whole Bedrock catalog (Roboto admins). */
  bedrockCatalog: () => [...orgQueryKeys.all(), "bedrockCatalog"] as const,

  /** Which catalog entry does each deployment-wide model role (Roboto admins). */
  bedrockRoles: () => [...orgQueryKeys.all(), "bedrockRoles"] as const,

  /** Prefix for every Roboto-admin search over orgs' model configurations. */
  modelConfigSearches: () =>
    [...orgQueryKeys.all(), "modelConfigSearch"] as const,
  modelConfigSearch: (filter: OrgModelConfigFilter, pageSize: number) =>
    [...orgQueryKeys.modelConfigSearches(), filter, pageSize] as const,
};
